import type { Channel } from '../../utils/get-channels-from-pypes';

export interface TestChannel {
  appId: string;
  channelEnv: 'live' | 'sandbox';
  name: string;
  /** A voice channel, tested through `appId` (see `listVoiceTestChannels`). */
  voiceChannelId?: string;
}

/**
 * Pick the channel a test conversation should run against.
 *
 * Pype binds to a CHANNEL, not a solution, so "test the current solution"
 * needs one chosen for the user. A solution routinely has two — a solution
 * created with default channels gets both "Live Pype" and "Sandbox Pype" — so
 * this is a real choice, not a formality.
 *
 * **Sandbox wins.** A test conversation is a real conversation: it opens a
 * session, runs the flow, and lands in analytics and transcripts like any
 * other. Defaulting to Live would put staff traffic into production data and,
 * for a solution wired to a live agent platform, could route a test chat to a
 * human. Sandbox is the environment that exists for exactly this.
 *
 * `widget` only: `twilio_voice` channels have no embeddable widget, so booting
 * one would leave an empty pane.
 *
 * Returns null when the solution has no testable channel — an unpublished
 * solution, or one whose only channel is voice. The caller shows the reason
 * rather than an empty box.
 *
 * This is only the DEFAULT, and safer is not the same as usable: a solution
 * published to Live but not Sandbox has a sandbox channel that never
 * establishes a session, and the symptom is a widget that boots and then will
 * not let you type. So the surface lets the channel be changed —
 * `listTestChannels` supplies the options.
 */
/** Every channel a conversation can actually be started against. */
export const listTestChannels = (
  channels: Channel[] | null | undefined
): TestChannel[] =>
  (channels ?? [])
    .filter(
      (channel) => (channel.type ?? 'widget') === 'widget' && !!channel.id
    )
    .map((channel) => ({
      appId: channel.id,
      channelEnv: channel.env,
      name: channel.name,
    }));

/**
 * Voice channels, testable in the browser. A `twilio_voice` channel takes calls
 * only — the Engagement API answers a browser session on it with 404 — so each
 * runs through the widget channel in its own environment: the same solution
 * and flow, spoken and heard in the browser rather than over the phone. A
 * voice channel with no widget beside it in that environment is left out.
 */
export const listVoiceTestChannels = (
  channels: Channel[] | null | undefined
): TestChannel[] => {
  const widgets = listTestChannels(channels);
  return (channels ?? [])
    .filter((channel) => channel.type === 'twilio_voice' && !!channel.id)
    .flatMap((voice) => {
      const via = widgets.find((w) => w.channelEnv === voice.env);
      return via
        ? [
            {
              appId: via.appId,
              channelEnv: voice.env,
              name: voice.name,
              voiceChannelId: voice.id,
            },
          ]
        : [];
    });
};

export const selectTestChannel = (
  channels: Channel[] | null | undefined
): TestChannel | null => {
  const testable = listTestChannels(channels);

  return (
    testable.find((channel) => channel.channelEnv === 'sandbox') ??
    testable[0] ??
    null
  );
};

/** Why there is nothing to test against, phrased for the empty state. */
export const describeNoTestChannel = (
  channels: Channel[] | null | undefined
): string => {
  if (channels === null || channels === undefined) {
    return 'Loading this solution’s channels…';
  }
  if (channels.length === 0) {
    return 'This solution has no channels yet. Create one to test it.';
  }

  return 'This solution has no widget channel. Voice channels cannot be tested here.';
};
